/**
 * Sync lookbook modal media playback so only the active slide plays.
 */

const LOOKBOOK_MODAL_SELECTOR = "dialog-component.lookbook-modal";
const LOOKBOOK_SLIDE_SELECTOR = "slideshow-slide";

/**
 * @param {Element | null} root
 * @returns {HTMLElement[]}
 */
function getSlides(root) {
  if (!(root instanceof Element)) return [];
  return Array.from(root.querySelectorAll(LOOKBOOK_SLIDE_SELECTOR));
}

/**
 * @param {HTMLElement} modal
 * @returns {HTMLElement | null}
 */
function getActiveSlide(modal) {
  const activeSlide = modal.querySelector(
    `${LOOKBOOK_SLIDE_SELECTOR}[aria-hidden="false"]`,
  );
  if (activeSlide instanceof HTMLElement) return activeSlide;

  const slides = getSlides(modal);
  return slides[0] || null;
}

/**
 * @param {HTMLVideoElement} video
 */
function playVideo(video) {
  const playPromise = video.play();
  if (playPromise && typeof playPromise.catch === "function") {
    playPromise.catch(() => {
      // Browsers may block autoplay until user interaction.
    });
  }
}

/**
 * @param {HTMLIFrameElement} iframe
 */
function playIframe(iframe) {
  const isYoutube = iframe.dataset.videoType === "youtube";
  const message = isYoutube
    ? '{"event":"command","func":"playVideo","args":""}'
    : '{"method":"play"}';

  iframe.contentWindow?.postMessage(message, "*");
}

/**
 * @param {HTMLIFrameElement} iframe
 */
function pauseIframe(iframe) {
  const isYoutube = iframe.dataset.videoType === "youtube";
  const message = isYoutube
    ? '{"event":"command","func":"pauseVideo","args":""}'
    : '{"method":"pause"}';

  iframe.contentWindow?.postMessage(message, "*");
}

/**
 * @param {HTMLElement} slide
 * @param {boolean} isActive
 */
function syncSlideMedia(slide, isActive) {
  const videos = Array.from(slide.querySelectorAll("video"));
  for (const video of videos) {
    if (!(video instanceof HTMLVideoElement)) continue;

    if (isActive) {
      playVideo(video);
    } else {
      video.pause();
    }
  }

  const iframes = Array.from(slide.querySelectorAll("iframe[data-video-type]"));
  for (const iframe of iframes) {
    if (!(iframe instanceof HTMLIFrameElement)) continue;

    if (isActive) {
      playIframe(iframe);
    } else {
      pauseIframe(iframe);
    }
  }
}

/**
 * @param {HTMLElement} modal
 * @param {HTMLElement | null} activeSlide
 */
function syncModalMedia(modal, activeSlide = null) {
  const selectedSlide = activeSlide || getActiveSlide(modal);
  if (!(selectedSlide instanceof HTMLElement)) return;

  const slides = getSlides(modal);
  for (const slide of slides) {
    const isActive = slide === selectedSlide;
    syncSlideMedia(slide, isActive);
  }
}

/**
 * @param {HTMLElement} modal
 */
function pauseModalMedia(modal) {
  const slides = getSlides(modal);
  for (const slide of slides) {
    syncSlideMedia(slide, false);
  }
}

/**
 * @param {Event} event
 */
function handleDialogOpen(event) {
  const modal = event.currentTarget;
  if (!(modal instanceof HTMLElement)) return;

  syncModalMedia(modal);
}

/**
 * @param {Event} event
 */
function handleDialogClose(event) {
  const modal = event.currentTarget;
  if (!(modal instanceof HTMLElement)) return;

  pauseModalMedia(modal);
}

/**
 * @param {Event} event
 */
function handleSlideshowSelect(event) {
  const modal = event.currentTarget;
  if (!(modal instanceof HTMLElement)) return;

  const detail = /** @type {{ slide?: Element } | undefined} */ (event.detail);
  const selectedSlide = detail?.slide;

  if (!(selectedSlide instanceof HTMLElement)) {
    syncModalMedia(modal);
    return;
  }

  syncModalMedia(modal, selectedSlide);
}

/**
 * @param {HTMLElement} modal
 */
function bindLookbookModal(modal) {
  if (modal.dataset.lookbookModalBound === "true") return;

  modal.dataset.lookbookModalBound = "true";
  modal.addEventListener("dialog:open", handleDialogOpen);
  modal.addEventListener("dialog:close", handleDialogClose);
  modal.addEventListener("slideshow:select", handleSlideshowSelect);
}

/**
 * @param {ParentNode} [root]
 */
function initializeLookbookModals(root = document) {
  const modals = root.querySelectorAll(LOOKBOOK_MODAL_SELECTOR);
  for (const modal of modals) {
    if (!(modal instanceof HTMLElement)) continue;
    bindLookbookModal(modal);
  }
}

initializeLookbookModals();

const lookbookMutationObserver = new MutationObserver((entries) => {
  for (const entry of entries) {
    for (const addedNode of entry.addedNodes) {
      if (!(addedNode instanceof HTMLElement)) continue;

      if (addedNode.matches(LOOKBOOK_MODAL_SELECTOR)) {
        bindLookbookModal(addedNode);
      }

      initializeLookbookModals(addedNode);
    }
  }
});

if (document.body) {
  lookbookMutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
